import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { memberAPI } from '../api/memberAPI';
import { regionAPI } from '../api/regionAPI';
import { Avatar } from '../components/Avatar';
import { FilterPopover } from '../components/FilterPopover';
import { FilterBar } from '../components/FilterBar';
import { useClickOutside } from '../hooks/useClickOutside';
import { timeAgo } from '../utils/format';

const buildPrayersMap = (data) => {
    const map = new Map();
    for (const pr of data) {
        if (!map.has(pr.member_id)) map.set(pr.member_id, { name: pr.member_name, regionId: pr.region_id, photoUrl: pr.photo_url, prayers: [] });
        map.get(pr.member_id).prayers.push(pr);
    }
    return map;
};

function ReactionAvatars({ reactions, myReaction, onReact, userName }) {
    const count = reactions.length;

    if (count === 0) {
        return (
            <button
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium border border-slate-200 text-slate-400 hover:border-accent-300 hover:text-accent-500 transition-all active:scale-95 dark:border-slate-600 dark:text-slate-500 dark:hover:border-accent-500 dark:hover:text-accent-400"
                onClick={(e) => { e.stopPropagation(); onReact(); }}
                aria-label="Pray"
            >
                <span className="text-[11px] leading-none">🙏</span>
                <span>Pray</span>
            </button>
        );
    }

    // Build description text
    const myIdx = reactions.findIndex((r) => r.leader_id === myReaction?.leader_id);
    const others = reactions.filter((_, i) => i !== myIdx);
    let text = '';
    if (myReaction) {
        if (others.length === 0) text = 'You are praying';
        else if (others.length === 1) text = `You & ${others[0].leader_name.split(' ')[0]} are praying`;
        else text = `You & ${others.length} others praying`;
    } else {
        if (count === 1) text = `${reactions[0].leader_name.split(' ')[0]} is praying`;
        else if (count === 2) text = `${reactions[0].leader_name.split(' ')[0]} & ${reactions[1].leader_name.split(' ')[0]} praying`;
        else text = `${reactions[0].leader_name.split(' ')[0]}, ${reactions[1].leader_name.split(' ')[0]} & ${count - 2} other${count - 2 > 1 ? 's' : ''} praying`;
    }

    const shown = reactions.slice(0, 3);
    const overflow = count - 3;

    return (
        <button
            className={`inline-flex items-center gap-1.5 rounded-full pl-0.5 pr-2.5 py-0.5 text-xs font-medium transition-all active:scale-95 ${myReaction ? 'bg-accent-50 text-accent-700 dark:bg-accent-900/30 dark:text-accent-300' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
            onClick={(e) => { e.stopPropagation(); onReact(); }}
            aria-label={myReaction ? 'Remove prayer reaction' : 'Add prayer reaction'}
        >
            <span className="inline-flex -space-x-1.5">
                {shown.map((r) => (
                    <Avatar
                        key={r.leader_id}
                        name={r.leader_name}
                        photoUrl={r.leader_photo_url}
                        size="xs"
                        className={`ring-2 ${r.leader_id === myReaction?.leader_id ? 'ring-accent-300 dark:ring-accent-600' : 'ring-white dark:ring-slate-800'}`}
                    />
                ))}
                {overflow > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-600 text-[9px] font-bold text-slate-600 dark:text-slate-300 ring-2 ring-white dark:ring-slate-800">
                        +{overflow}
                    </span>
                )}
            </span>
            <span>{text}</span>
        </button>
    );
}

function PrayerBubble({ pr, memberId, myReaction, onReact, onDelete, onDoubleTap, userName }) {
    const reactions = pr.reactions || [];

    return (
        <div
            className="text-sm bg-slate-50 dark:bg-slate-700/50 rounded-2xl px-3 py-2 select-none"
            onDoubleClick={() => onReact()}
            onTouchEnd={() => onDoubleTap()}
        >
            <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-slate-400 dark:text-slate-500">{timeAgo(pr.created_at)}</span>
                <button
                    className="text-slate-300 hover:text-red-500 text-base leading-none flex-shrink-0 transition-colors opacity-60 hover:opacity-100 dark:text-slate-500 dark:hover:text-red-400"
                    onClick={() => onDelete()}
                    aria-label="Delete"
                >
                    &times;
                </button>
            </div>
            <p className="text-slate-700 dark:text-slate-300 leading-relaxed">{pr.text}</p>
            <div className="mt-2">
                <ReactionAvatars reactions={reactions} myReaction={myReaction} onReact={onReact} userName={userName} />
            </div>
        </div>
    );
}

export function PrayerRequests() {
    const { isAuthenticated, user, isAdmin } = useAuth();
    const navigate = useNavigate();

    const [prayers, setPrayers] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [regions, setRegions] = useState([]);
    const [viewMode, setViewMode] = useState('grid');
    const [regionFilter, setRegionFilter] = useState('');

    const [addingFor, setAddingFor] = useState({});
    const [addSearch, setAddSearch] = useState('');
    const [addResults, setAddResults] = useState([]);
    const [addMember, setAddMember] = useState(null);
    const [addText, setAddText] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const searchTimer = useRef(null);
    const searchRef = useRef(null);
    const searchInputRef = useRef(null);
    const lastTapRef = useRef({});

    useEffect(() => {
        if (!isAuthenticated) return;
        regionAPI.getRegions().then((res) => { setRegions((res.data.data || {}).items || []); }).catch(() => {});
    }, [isAuthenticated]);

    // Initial REST fetch (immediate data while SSE connects)
    useEffect(() => {
        if (!isAuthenticated) return;
        memberAPI.getAllPrayerRequests().then((res) => {
            setPrayers(buildPrayersMap(res.data.data || []));
            setError(null);
        }).catch((err) => {
            setError(err.response?.data?.message || 'Failed to load prayer requests');
        }).finally(() => setLoading(false));
    }, [isAuthenticated]);

    // SSE subscription for real-time updates
    useEffect(() => {
        if (!isAuthenticated) return;
        const es = new EventSource(memberAPI.prayerStreamUrl());
        es.onmessage = (e) => {
            try {
                const data = JSON.parse(e.data);
                setPrayers(buildPrayersMap(data));
                setLoading(false);
                setError(null);
            } catch { /* ignore parse errors */ }
        };
        es.onerror = () => {
            // EventSource auto-reconnects; no action needed
        };
        return () => es.close();
    }, [isAuthenticated]);

    const refreshPrayers = () => {
        memberAPI.getAllPrayerRequests().then((res) => {
            setPrayers(buildPrayersMap(res.data.data || []));
        }).catch(() => {});
    };

    const regionName = (rid) => { const r = regions.find((r) => r.id === rid); return r ? r.name : ''; };

    const handleDelete = async (memberId, prId) => {
        try { await memberAPI.deletePrayerRequest(memberId, prId); }
        catch (err) { setError(err.response?.data?.message || 'Failed to delete prayer request'); }
    };

    const handleInlineAdd = async (memberId) => {
        const text = (addingFor[memberId] || '').trim();
        if (!text) return;
        try {
            await memberAPI.createPrayerRequest(memberId, { text });
            setAddingFor((prev) => ({ ...prev, [memberId]: '' }));
            refreshPrayers();
        } catch (err) { setError(err.response?.data?.message || 'Failed to add prayer request'); }
    };

    const handleReaction = async (memberId, prId) => {
        try { await memberAPI.togglePrayerReaction(memberId, prId); }
        catch (err) { setError(err.response?.data?.message || 'Failed to toggle reaction'); }
    };

    // Double-tap handler for mobile
    const handleDoubleTap = (memberId, prId) => {
        const now = Date.now();
        const key = prId;
        if (lastTapRef.current[key] && now - lastTapRef.current[key] < 350) {
            handleReaction(memberId, prId);
            lastTapRef.current[key] = 0;
        } else {
            lastTapRef.current[key] = now;
        }
    };

    useEffect(() => {
        if (!addSearch.trim()) { setAddResults([]); return; }
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const res = await memberAPI.getMembers({ q: addSearch, per_page: 8 });
                setAddResults((res.data.data || {}).items || []);
            } catch { setAddResults([]); }
            finally { setSearchLoading(false); }
        }, 300);
        return () => clearTimeout(searchTimer.current);
    }, [addSearch]);

    useClickOutside(searchRef, useCallback(() => setAddResults([]), []));

    const handleSelectMember = (m) => { setAddMember(m); setAddSearch(''); setAddResults([]); };

    const handleAddForNewMember = async () => {
        if (!addMember || !addText.trim()) return;
        try {
            await memberAPI.createPrayerRequest(addMember.id, { text: addText.trim() });
            setAddMember(null); setAddText('');
            setSuccess(`Prayer request added for ${addMember.name}`);
            setTimeout(() => setSuccess(null), 3000);
            refreshPrayers();
        } catch (err) { setError(err.response?.data?.message || 'Failed to add prayer request'); }
    };

    if (!isAuthenticated) {
        return (
            <div className="flex items-center justify-center min-h-[60vh] p-4">
                <Card><CardBody><h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">Not authenticated</h2><p className="text-sm text-slate-500 mb-4">Please login.</p><Button onClick={() => navigate('/login')}>Login</Button></CardBody></Card>
            </div>
        );
    }

    const allEntries = [...prayers.entries()];
    const memberEntries = regionFilter ? allEntries.filter(([, e]) => e.regionId === regionFilter) : allEntries;
    const totalPrayers = memberEntries.reduce((sum, [, e]) => sum + e.prayers.length, 0);

    return (
        <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Prayer Requests</h1>
                    <p className="text-sm text-slate-500 mt-0.5">{totalPrayers} request{totalPrayers !== 1 ? 's' : ''} for {memberEntries.length} member{memberEntries.length !== 1 ? 's' : ''}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" onClick={() => navigate('/dashboard')}>Back</Button>
                </div>
            </div>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            {/* Toolbar */}
            <FilterBar
                search={
                    <div className="w-full sm:w-auto sm:min-w-[240px] sm:max-w-[320px] relative" ref={searchRef}>
                        {!addMember ? (
                            <div className="relative">
                                <input
                                    ref={searchInputRef}
                                    className="input-field pr-8"
                                    placeholder="Search member to add request..."
                                    value={addSearch}
                                    onChange={(e) => setAddSearch(e.target.value)}
                                />
                                {searchLoading && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">...</span>}
                                {addResults.length > 0 && (
                                    <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-64 overflow-auto">
                                        {addResults.map((m) => (
                                            <div key={m.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors" onClick={() => handleSelectMember(m)}>
                                                <Avatar name={m.name} photoUrl={m.photo_url} size="sm" />
                                                <span className="text-sm text-slate-700 dark:text-slate-300">{m.name}</span>
                                                {regionName(m.region_id) && <span className="text-xs text-slate-400 ml-auto">{regionName(m.region_id)}</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="card-base p-3 space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Avatar name={addMember.name} photoUrl={addMember.photo_url} size="sm" />
                                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{addMember.name}</span>
                                    </div>
                                    <button className="text-slate-400 hover:text-slate-600 text-lg" onClick={() => { setAddMember(null); setAddText(''); }}>&times;</button>
                                </div>
                                <div className="flex gap-2">
                                    <input
                                        className="input-field flex-1"
                                        placeholder="Enter prayer request..."
                                        value={addText}
                                        onChange={(e) => setAddText(e.target.value)}
                                        maxLength={280}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddForNewMember(); }}
                                        autoFocus
                                    />
                                    <Button size="sm" onClick={handleAddForNewMember} disabled={!addText.trim()}>Add</Button>
                                </div>
                            </div>
                        )}
                    </div>
                }
                filters={isAdmin && regions.length > 0 ? (
                    <FilterPopover
                        label="Region"
                        value={regionFilter}
                        onChange={setRegionFilter}
                        options={regions.map((r) => ({ value: r.id, label: r.name }))}
                        allLabel="All Regions"
                    />
                ) : null}
                actions={
                    <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                        <button className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'grid' ? 'bg-accent-500 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`} onClick={() => setViewMode('grid')} title="Grid view">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>
                        </button>
                        <button className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-accent-500 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`} onClick={() => setViewMode('list')} title="List view">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="2" width="14" height="2.5" rx="1"/><rect x="1" y="6.75" width="14" height="2.5" rx="1"/><rect x="1" y="11.5" width="14" height="2.5" rx="1"/></svg>
                        </button>
                    </div>
                }
                activeFilterCount={regionFilter ? 1 : 0}
                onClearFilters={() => setRegionFilter('')}
            />

            {loading ? (
                <p className="text-center text-slate-500 py-6">Loading...</p>
            ) : memberEntries.length === 0 ? (
                /* Empty state */
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 rounded-full bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center mb-4">
                        <span className="text-2xl">🙏</span>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-1">No prayer requests yet</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 max-w-xs">Search for a member above to add the first prayer request for your group.</p>
                    <Button size="sm" onClick={() => searchInputRef.current?.focus()}>Add First Prayer Request</Button>
                </div>
            ) : viewMode === 'grid' ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {memberEntries.map(([memberId, entry]) => (
                        <div key={memberId} className="card-base p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <Avatar name={entry.name} photoUrl={entry.photoUrl} size="md" />
                                <div className="flex-1 min-w-0">
                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 block truncate">{entry.name}</span>
                                    {regionName(entry.regionId) && <span className="text-xs text-slate-400">{regionName(entry.regionId)}</span>}
                                </div>
                                <span className="text-xs text-slate-400 font-medium">{entry.prayers.length}/3</span>
                            </div>
                            <div className="space-y-2 mb-3">
                                {entry.prayers.map((pr) => {
                                    const reactions = pr.reactions || [];
                                    const myReaction = reactions.find((r) => r.leader_id === user?.id);
                                    return (
                                        <PrayerBubble
                                            key={pr.id}
                                            pr={pr}
                                            memberId={memberId}
                                            myReaction={myReaction}
                                            onReact={() => handleReaction(memberId, pr.id)}
                                            onDelete={() => handleDelete(memberId, pr.id)}
                                            onDoubleTap={() => handleDoubleTap(memberId, pr.id)}
                                            userName={user?.name}
                                        />
                                    );
                                })}
                            </div>
                            {entry.prayers.length < 3 && (
                                <div className="border-t border-slate-100 dark:border-slate-700/50 pt-2">
                                    <div className="flex gap-2">
                                        <input
                                            className="input-field flex-1 !min-h-[32px]"
                                            placeholder={entry.prayers.length === 0 ? 'Add a prayer request...' : 'Add another request...'}
                                            value={addingFor[memberId] || ''}
                                            onChange={(e) => setAddingFor((prev) => ({ ...prev, [memberId]: e.target.value }))}
                                            maxLength={280}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleInlineAdd(memberId); }}
                                        />
                                        <button
                                            className="w-8 h-8 rounded-lg bg-accent-500 text-white font-bold text-sm hover:bg-accent-600 disabled:opacity-50 transition-colors flex-shrink-0"
                                            onClick={() => handleInlineAdd(memberId)}
                                            disabled={!(addingFor[memberId] || '').trim()}
                                        >+</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="space-y-3">
                    {memberEntries.map(([memberId, entry]) => (
                        <div key={memberId} className="card-base p-4">
                            <div className="flex items-center gap-3 mb-2">
                                <Avatar name={entry.name} photoUrl={entry.photoUrl} size="md" />
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{entry.name}</span>
                                {regionName(entry.regionId) && <span className="text-xs text-slate-400 ml-auto">{regionName(entry.regionId)}</span>}
                            </div>
                            <div className="space-y-2">
                                {entry.prayers.map((pr) => {
                                    const reactions = pr.reactions || [];
                                    const myReaction = reactions.find((r) => r.leader_id === user?.id);
                                    return (
                                        <PrayerBubble
                                            key={pr.id}
                                            pr={pr}
                                            memberId={memberId}
                                            myReaction={myReaction}
                                            onReact={() => handleReaction(memberId, pr.id)}
                                            onDelete={() => handleDelete(memberId, pr.id)}
                                            onDoubleTap={() => handleDoubleTap(memberId, pr.id)}
                                            userName={user?.name}
                                        />
                                    );
                                })}
                                {entry.prayers.length < 3 && (
                                    <div className="border-t border-slate-100 dark:border-slate-700/50 pt-2 mt-2">
                                        <div className="flex gap-2">
                                            <input
                                                className="input-field flex-1 !min-h-[32px]"
                                                placeholder={entry.prayers.length === 0 ? 'Add a prayer request...' : 'Add another request...'}
                                                value={addingFor[memberId] || ''}
                                                onChange={(e) => setAddingFor((prev) => ({ ...prev, [memberId]: e.target.value }))}
                                                maxLength={280}
                                                onKeyDown={(e) => { if (e.key === 'Enter') handleInlineAdd(memberId); }}
                                            />
                                            <button
                                                className="w-8 h-8 rounded-lg bg-accent-500 text-white font-bold text-sm hover:bg-accent-600 disabled:opacity-50 transition-colors flex-shrink-0"
                                                onClick={() => handleInlineAdd(memberId)}
                                                disabled={!(addingFor[memberId] || '').trim()}
                                            >+</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default PrayerRequests;
